import { AppConfig } from 'src/app/app-config.service';
import { ItemClass, ItemClassService } from './ItemClass.service';
import { AcumaticaFileReference, AcumaticaRequestOptions, AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService } from './acumatica-entity';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Acumatica, AcumaticaFieldFormat } from '../acumatica-field-decorator';

@Injectable({
    providedIn: 'root'
})

export class StockItemService extends AcumaticaEntityService {
    constructor(
        protected acumatica: AcumaticaService,
        private itemClasses: ItemClassService,
    ) {
        super(acumatica, 'StockItem', StockItem);
    }

    // given an item, read in the Primary Item (for memo Items)
    // and the Item Class
    private supplementItem(
        item: StockItem,
        getItemClassEntity: boolean,
        successHandler: (item: StockItem) => void,
        errorHandler: (error: any) => void
    ) {
        // define function first to retrieve Item Class
        let getPrimaryItemClass = (getItemClassEntity && item.PrimaryItemClassID)
            ? () => {
                this.itemClasses.get(item.PrimaryItemClassID!, {loader: null})
                .subscribe((primaryItemClass: ItemClass) => {
                    item.ItemClass = primaryItemClass;
                    // then notify the end recipient
                    successHandler(item);
                });
            }
            : () => {
                successHandler(item);
            }

        // for memo items
        if (item.isMemoItem) {
            // get the primary item first
            super.getEntity(item.PrimaryInventoryID, {loader: null})
            .subscribe(
                (primaryItem: StockItem) => {
                    item.PrimaryItem = primaryItem;
                    // then get the primary item class
                    getPrimaryItemClass();
                },
                errorHandler
            );
        } else {
            // non-memos, can immediately get the primary item class
            getPrimaryItemClass();
        }
    }

    get(
        InventoryID: string,
        options: AcumaticaRequestOptions = {},
        getItemClassEntity: boolean = true
    ): Observable<StockItem> {
        // Create and return a new custom Observable
        // so we can implement additional StockItem getter logic

        return new Observable<StockItem>(subscriber => {
            // ensure files are retrieved (to access Acumatica product image URL)
            if (typeof options.expand === 'string') options.expand = [options.expand];
            if (!options.expand) options.expand = [];
            if ((<string[]>options.expand).indexOf('files') === -1) options.expand.push('files');

            // Get the Inventory Item
            super.getEntity(InventoryID, {
                ...{ loader: { message: "Getting Inventory Item" } },
                ...options,
            }).subscribe({
                next: (item: StockItem) => {
                    this.supplementItem(item, getItemClassEntity,
                        (item: StockItem) => {
                            subscriber.next(item);
                            subscriber.complete();
                        },
                        error => {
                            console.error(error);
                            // even if getting the 'primary' item fails, return the memo item info
                            subscriber.next(item);
                            subscriber.complete();
                        });
                },
                error: error => subscriber.error(error),
            });
        });
    }

    getSimple(
        InventoryID: string,
        options: AcumaticaRequestOptions = {}
    ): Observable<StockItem>{
        options = {...{loader:{message:"Getting Inventory Item"}}, ...options};
        return super.getEntity(InventoryID, options);
    }

    getItems(
        filter: string,
        options: AcumaticaRequestOptions = {}
    ): Observable<StockItem[]> {
        return super.getEntities(filter, options);
    }

    // returns promise to ensure invocation even if client does not re-subscribe
    put(item: StockItem): Promise<StockItem> {
        return new Promise<StockItem>(resolve => {
            // Save the modified item -- merge it back into the original item
            // to retain any additional custom fields (UsrLifeCycle)
            return super.putEntity(item, undefined)
                .subscribe(item => this.supplementItem(item, true,
                    (item: StockItem) => {
                        resolve(item);
                    },
                    (error: any) => {
                        throw error;
                    }
                ));
        });
    }
}

export enum ItemType {
    Unspecified = 0,
    MemoSample = 1,
    RolledGood = 2,
    Supplies = 3,
    SampleCard = 4,
    RepSample = 5,
    TagSet = 6,
    CollatedSet = 7,
    EmptyBinderShell = 8,
    FullCardBinder = 9,
}

export class StockItem extends AcumaticaEntity {
    @Acumatica() InventoryID!: string;
    @Acumatica() note!: string;

    @Acumatica("ItemClass") ItemClassID!: string;
    @Acumatica() PatternClassID?: string;
    @Acumatica({ write: false }) ItemClass?: ItemClass;
    @Acumatica() Description!: string | null;

    @Acumatica() DefaultWarehouseID!: string;
    @Acumatica() ItemStatus!: string;
    @Acumatica() BaseUOM!: string;
    @Acumatica() PurchaseUOM!: string;
    @Acumatica() SalesUOM!: string;

    @Acumatica("DefaultIssueLocationID") DefaultIssueFrom!: string | null;
    @Acumatica() DefaultPrice!: number | null;
    @Acumatica() IsAKit!: boolean;
    @Acumatica() LotSerialClass!: string;
    @Acumatica() MSRP!: number | null;
    // @Acumatica({ custom: { view: "ItemSettings", field: "UsrLifeCycle" } }) LifeCycle!: string | null;
    @Acumatica() LifeCycle!: string | null;
    @Acumatica({ format: AcumaticaFieldFormat.Date }) LastModified!: Date;
    @Acumatica() ImageUrl!: string | null;
    @Acumatica({ write: false }) ColorName!: string | null;

    @Acumatica(AcumaticaFileReference) files?: AcumaticaFileReference[];

    public static getItemType(
        LotSerialClass: string,
        ItemClassID: string,
        InventoryID: string,
        ItemDescription: string
    ): ItemType {
        if (<any>InventoryID === undefined) return ItemType.Unspecified;
        if (LotSerialClass === 'R') return ItemType.RolledGood;

        // Tag Set items are assigned Item Class '50' (Memo samples) -- special Memo Sample handling causes issues
        // (can't fetch stock item data by truncating first character of Inventory ID)
        if (InventoryID.substring(InventoryID.length - 2) == 'TS' && (ItemDescription || '').indexOf(' TAG SET ') > -1)
            return ItemType.TagSet;

        switch (ItemClassID.trim().toUpperCase()) {
            case '50': return ItemType.MemoSample;
            case '20': return ItemType.Supplies;
            case 'CD': return ItemType.SampleCard;
            case 'CL': return ItemType.CollatedSet;
            case 'RS': return ItemType.RepSample;
            case 'EBND': return ItemType.EmptyBinderShell;
            case 'BND': return ItemType.FullCardBinder;
            case 'TAG': return ItemType.TagSet;
            default:
                return ItemType.Unspecified;
        }
    }

    public static getItemTypeDescription(type: ItemType) {
        switch (type) {
            case ItemType.MemoSample: return "Memo Samples";
            case ItemType.Supplies: return "Supplies";
            case ItemType.SampleCard: return "Sample Cards";
            case ItemType.CollatedSet: return "Collated Sets";
            case ItemType.RepSample: return "Rep Samples";
            case ItemType.EmptyBinderShell: return "Empty Binder Shells";
            case ItemType.FullCardBinder: return "Full Sample Card Binders"
            default: return "(unknown)"
        }
    }

    get ItemType(): ItemType {
        return StockItem.getItemType(this.LotSerialClass, this.ItemClassID, this.InventoryID, this.Description || '');
    }
    /** returns boolean value indicating if the item is a Lot or Serial number tracked item */
    get isLotSerItem(): boolean {
        return this.LotSerialClass !== 'N';
    }
    get isRolledGood(): boolean {
        return this.ItemType == ItemType.RolledGood;
    }
    get isMemoItem(): boolean {
        return this.ItemType == ItemType.MemoSample;
    }
    get PrimaryInventoryID(): string {
        return (this.isMemoItem ? this.InventoryID?.substring(1) : this.InventoryID) || '';
    }

    private _PrimaryItem?: StockItem;
    get PrimaryItem(): StockItem { return this._PrimaryItem || this; }
    set PrimaryItem(value: StockItem) { this._PrimaryItem = value; }

    get PrimaryItemClassID(): string {
        return (this.isMemoItem ? this.PrimaryItem?.ItemClassID : this.ItemClassID) || "";
    }

    get AcumaticaImageUrl(): string {
        const url = new URL(AppConfig.settings.acumatica.URL);
        const item = this.PrimaryItem || this;
        const file = item.files?.find(file => file.__filename == this.ImageUrl);
        return file ? (url.origin + file.__href) : '/assets/img/ImageUnavailable.svg';
    }

    ProductImageURL(quality = 60, size = 80): string {
        let item = this.PrimaryItem || this;
        if (item.isRolledGood) {
            // return `${AppConfig.settings.LiquiFire.ImageServer}?set=item[${item.ItemClassID}/${item.InventoryID}]&set=key[quality],value[${quality}]&set=size[${size}]&call=url[file:/product_tn.chain]`
            let server = AppConfig.settings.LiquiFire.ImageServer;
            if (server.endsWith("/")) server = server.substring(0, server.length - 1);
            return `${server}/item/${item.InventoryID}/image/thumbnail?size=${size}&quality=${quality}`
        } else {
            return this.AcumaticaImageUrl;
        }
    }

    /** returns the rounding precesion for quantities; Rolls to 0.125, other Lot items to 0.001 */
    get QtyRoundingPrecision(): number { return (this.LotSerialClass == 'R') ? 0.125 : 0.001; }

    roundQty(value?: number | string | null): number | undefined | null {
        if (value === null) return null;
        if (typeof value == 'string') value = parseFloat(value);
        value = <number>value;
        if (value === undefined || this.QtyRoundingPrecision === null) return value;

        // Eighth rounding hacks
        if (this.QtyRoundingPrecision == 0.125) {
            switch (value * 10 % 10) {
                case 0: value = Math.floor(value); break;
                case 1: value = Math.floor(value) + 0.125; break;
                case 2: value = Math.floor(value) + 0.25; break;
                case 3: value = Math.floor(value) + 0.375; break;
                case 4: value = Math.floor(value) + 0.5; break;
                case 5: value = Math.floor(value) + 0.5; break;
                case 6: value = Math.floor(value) + 0.625; break;
                case 7: value = Math.floor(value) + 0.75; break;
                case 8: value = Math.floor(value) + 0.875; break;
                case 9: value = Math.floor(value) + 0.875; break;
            }
        }

        return Math.roundTo(value, this.QtyRoundingPrecision);
    }
}